
import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import axios from "axios";

const HomeBannerData = () => {
  const navigate = useNavigate();

  const [activeLanguage, setActiveLanguage] = useState('en');
  const [contentId, setContentId] = useState(null);
  const [isNewContent, setIsNewContent] = useState(false);

  // English (default) form data
  const [formData, setFormData] = useState({
    banner: {
      tag: '',
      headline: ''
    },
    stats: {
      miles: '',
      hotelRooms: '',
      retail: '',
      office: '',
      miles_description: '',
      hotelRooms_description: '',
      retail_description: '',
      office_description: ''
    },
    about: ['']
  });

  // Spanish translation form data
  const [spanishData, setSpanishData] = useState({
    banner: {
      tag: '',
      headline: ''
    },
    stats: {
      miles: '',
      hotelRooms: '',
      retail: '',
      office: ''
    },
    about: ['']
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchHomeBanner = async () => {
      try {
        // Fetch all contents (should only be one)
        const response = await axios.get(`${config.BASE_URL}/api/admin/home-banner?includeSpanish=true`, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.data && response.data.data.length > 0) {
          // Content exists, populate form
          const data = response.data.data[0];
          setContentId(data._id);
          setIsNewContent(false);

          setFormData({
            banner: {
              tag: data.banner?.tag || '',
              headline: data.banner?.headline || ''
            },
            stats: {
              miles: data.stats?.miles || '',
              hotelRooms: data.stats?.hotelRooms || '',
              retail: data.stats?.retail || '',
              office: data.stats?.office || '',
              miles_description: data.stats?.miles_description || '',
              hotelRooms_description: data.stats?.hotelRooms_description || '',
              retail_description: data.stats?.retail_description || '',
              office_description: data.stats?.office_description || ''
            },
            about: data.about && data.about.length > 0 ? data.about : ['']
          });

          setSpanishData({
            banner: {
              tag: data.spanishData?.banner?.tag || '',
              headline: data.spanishData?.banner?.headline || ''
            },
            stats: {
              miles: data.spanishData?.stats?.miles || '',
              hotelRooms: data.spanishData?.stats?.hotelRooms || '',
              retail: data.spanishData?.stats?.retail || '',
              office: data.spanishData?.stats?.office || '',
              miles_description: data.spanishData?.stats?.miles_description || '',
              hotelRooms_description: data.spanishData?.stats?.hotelRooms_description || '',
              retail_description: data.spanishData?.stats?.retail_description || '',
              office_description: data.spanishData?.stats?.office_description || ''
            },
            about: data.spanishData?.about && data.spanishData.about.length > 0 ? data.spanishData.about : ['']
          });
        } else {
          // No content exists, show empty form for creation
          setIsNewContent(true);
        }
      } catch (err) {
        console.error('Error fetching home banner:', err);

        if (err.response?.status === 404 || err.response?.status === 400) {
          // No content found, set as new
          setIsNewContent(true);
        } else if (err.response?.status === 401) {
          setErrors({ submit: 'Authentication failed. Please login again.' });
          setTimeout(() => {
            navigate('/admin/login');
          }, 2000);
        } else {
          setErrors({ submit: 'Failed to fetch home banner details' });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHomeBanner();
  }, [navigate]);

  const handleBannerChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      banner: {
        ...prev.banner,
        [field]: value
      }
    }));

    if (errors[`banner.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`banner.${field}`]: ''
      }));
    }
  };

  const handleStatsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        [field]: value
      }
    }));

    if (errors[`stats.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`stats.${field}`]: ''
      }));
    }
  };

  const handleAboutChange = (index, value) => {
    const newAbout = [...formData.about];
    newAbout[index] = value;
    setFormData(prev => ({
      ...prev,
      about: newAbout
    }));
  };

  const addAboutField = () => {
    setFormData(prev => ({
      ...prev,
      about: [...prev.about, '']
    }));
  };

  const removeAboutField = (index) => {
    if (formData.about.length > 1) {
      const newAbout = formData.about.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        about: newAbout
      }));
    }
  };

  // Spanish handlers
  const handleSpanishBannerChange = (field, value) => {
    setSpanishData(prev => ({
      ...prev,
      banner: {
        ...prev.banner,
        [field]: value
      }
    }));
  };

  const handleSpanishStatsChange = (field, value) => {
    setSpanishData(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        [field]: value
      }
    }));
  };

  const handleSpanishAboutChange = (index, value) => {
    const newAbout = [...spanishData.about];
    newAbout[index] = value;
    setSpanishData(prev => ({
      ...prev,
      about: newAbout
    }));
  };

  const addSpanishAboutField = () => {
    setSpanishData(prev => ({
      ...prev,
      about: [...prev.about, '']
    }));
  };

  const removeSpanishAboutField = (index) => {
    if (spanishData.about.length > 1) {
      const newAbout = spanishData.about.filter((_, i) => i !== index);
      setSpanishData(prev => ({
        ...prev,
        about: newAbout
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate English content
    // if (!formData.banner.tag.trim()) {
    //   newErrors['banner.tag'] = 'Banner tag is required';
    // }
    // if (!formData.banner.headline.trim()) {
    //   newErrors['banner.headline'] = 'Banner headline is required';
    // }
    // if (!formData.stats.miles.trim()) {
    //   newErrors['stats.miles'] = 'Miles stat is required';
    // }
    // if (!formData.stats.hotelRooms.trim()) {
    //   newErrors['stats.hotelRooms'] = 'Hotel rooms stat is required';
    // }
    // if (!formData.stats.retail.trim()) {
    //   newErrors['stats.retail'] = 'Retail stat is required';
    // }
    // if (!formData.stats.office.trim()) {
    //   newErrors['stats.office'] = 'Office stat is required';
    // }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const payload = {
        language: 'en',
        banner: {
          tag: formData.banner.tag.trim(),
          headline: formData.banner.headline.trim()
        },
        stats: {
          miles: formData.stats.miles.trim(),
          hotelRooms: formData.stats.hotelRooms.trim(),
          retail: formData.stats.retail.trim(),
          office: formData.stats.office.trim(),
          miles_description: formData.stats.miles_description.trim(),
          hotelRooms_description: formData.stats.hotelRooms_description.trim(),
          retail_description: formData.stats.retail_description.trim(),
          office_description: formData.stats.office_description.trim()

        },
        about: formData.about.filter(item => item.trim()).map(item => item.trim()),
        spanishData: JSON.stringify({
          language: 'es',
          banner: {
            tag: spanishData.banner.tag.trim(),
            headline: spanishData.banner.headline.trim()
          },
          stats: {
            miles: spanishData.stats.miles.trim(),
            hotelRooms: spanishData.stats.hotelRooms.trim(),
            retail: spanishData.stats.retail.trim(),
            office: spanishData.stats.office.trim(),
            miles_description: spanishData.stats.miles_description.trim(),
            hotelRooms_description: spanishData.stats.hotelRooms_description.trim(),
            retail_description: spanishData.stats.retail_description.trim(),
            office_description: spanishData.stats.office_description.trim()

          },
          about: spanishData.about.filter(item => item.trim()).map(item => item.trim())
        })
      };

      let response;

      if (isNewContent) {
        // Create new content
        response = await axios.post(`${config.BASE_URL}/api/admin/home-banner`, payload, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json"
          }
        });

        if (response.status === 201) {
          setContentId(response.data.data._id);
          setIsNewContent(false);
          setSuccessMessage('Home banner created successfully!');
        }
      } else {
        // Update existing content
        response = await axios.put(`${config.BASE_URL}/api/admin/home-banner/${contentId}`, payload, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json"
          }
        });

        if (response.status === 200) {
          setSuccessMessage('Home banner updated successfully!');
        }
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error saving home banner:', err);

      let errorMessage = "Failed to save home banner";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 400) {
        errorMessage = "Invalid data provided";
      } else if (err.response?.status === 404) {
        errorMessage = "Home banner not found";
      } else if (err.response?.status === 401) {
        errorMessage = "Authentication failed. Please login again.";
        setTimeout(() => {
          navigate('/admin/login');
        }, 2000);
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="container">
          <div className="main-section">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p className="text-white">Loading home banner...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        <h1 className="dash_title">Home Banner Data</h1>
        <div className="main-section">
          <div className="back_btn_wrap">
            <button className="back-btn" onClick={handleBackButton}>Go Back</button>
          </div>
          <div className="main-inner add_project_main">
            <h2 style={{ marginBottom: '20px', color: '#fff' }}>
              {isNewContent ? 'Create Home Banner' : 'Edit Home Banner'}
            </h2>

            {/* Language Tabs */}
            <div className="language-tabs" style={{ marginBottom: '20px' }}>
              <button
                type="button"
                className={`tab-btn ${activeLanguage === 'en' ? 'active' : ''}`}
                onClick={() => setActiveLanguage('en')}
                style={{
                  padding: '10px 20px',
                  marginRight: '10px',
                  backgroundColor: activeLanguage === 'en' ? '#007bff' : '#f8f9fa',
                  color: activeLanguage === 'en' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  cursor: 'pointer',
                  borderRadius: '4px'
                }}
              >
                English
              </button>
              <button
                type="button"
                className={`tab-btn ${activeLanguage === 'es' ? 'active' : ''}`}
                onClick={() => setActiveLanguage('es')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: activeLanguage === 'es' ? '#007bff' : '#f8f9fa',
                  color: activeLanguage === 'es' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  cursor: 'pointer',
                  borderRadius: '4px'
                }}
              >
                Spanish
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="language-content">
                {successMessage && (
                  <div style={{
                    padding: '12px',
                    marginBottom: '20px',
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    color: '#155724'
                  }}>
                    {successMessage}
                  </div>
                )}

                {/* English Content */}
                {activeLanguage === 'en' && (
                  <div className="language-content">
                    <h3 style={{ color: '#fff', marginBottom: '15px' }}>English Content</h3>

                    {/* Banner Section */}
                    <div className="section-group">
                      <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#ccc' }}>Banner</h4>
                      <div className="add_field titleField">
                        <h2>Tag *</h2>
                        <input
                          type="text"
                          value={formData.banner.tag}
                          onChange={(e) => handleBannerChange('tag', e.target.value)}
                          placeholder="Enter banner tag"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['banner.tag'] && (
                          <p className="error-text">{errors['banner.tag']}</p>
                        )}
                      </div>
                      <div className="add_field titleField">
                        <h2>Headline *</h2>
                        <input
                          type="text"
                          value={formData.banner.headline}
                          onChange={(e) => handleBannerChange('headline', e.target.value)}
                          placeholder="Enter banner headline"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['banner.headline'] && (
                          <p className="error-text">{errors['banner.headline']}</p>
                        )}
                      </div>
                    </div>

                    {/* Stats Section */}
                    <div className="section-group">
                      <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#ccc' }}>Stats</h4>
                      <div className="add_field titleField">
                        <h2>Miles *</h2>
                        <input
                          type="text"
                          value={formData.stats.miles}
                          onChange={(e) => handleStatsChange('miles', e.target.value)}
                          placeholder="e.g., 12,000"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['stats.miles'] && (
                          <p className="error-text">{errors['stats.miles']}</p>
                        )}
                      </div>
                      <div className="add_field titleField">
                        <h2>Miles Description *</h2>
                        <input
                          type="text"
                          value={formData.stats.miles_description}
                          onChange={(e) => handleStatsChange('miles_description', e.target.value)}
                          placeholder=""
                          required
                          disabled={isSubmitting}
                        />

                      </div>
                      <div className="add_field titleField">
                        <h2>Hotel Rooms *</h2>
                        <input
                          type="text"
                          value={formData.stats.hotelRooms}
                          onChange={(e) => handleStatsChange('hotelRooms', e.target.value)}
                          placeholder="e.g., 500"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['stats.hotelRooms'] && (
                          <p className="error-text">{errors['stats.hotelRooms']}</p>
                        )}
                      </div>
                      <div className="add_field titleField">
                        <h2>Hotel Rooms Description *</h2>
                        <input
                          type="text"
                          value={formData?.stats?.hotelRooms_description}
                          onChange={(e) => handleStatsChange('hotelRooms_description', e.target.value)}
                          placeholder=""
                          required
                          disabled={isSubmitting}
                        />
                        {errors['stats.hotelRooms'] && (
                          <p className="error-text">{errors['stats.hotelRooms']}</p>
                        )}
                      </div>
                      <div className="add_field titleField">
                        <h2>Retail *</h2>
                        <input
                          type="text"
                          value={formData.stats.retail}
                          onChange={(e) => handleStatsChange('retail', e.target.value)}
                          placeholder="e.g., 2M sq ft"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['stats.retail'] && (
                          <p className="error-text">{errors['stats.retail']}</p>
                        )}
                      </div>
                      <div className="add_field titleField">
                        <h2>Retail Description *</h2>
                        <input
                          type="text"
                          value={formData.stats.retail_description}
                          onChange={(e) => handleStatsChange('retail_description', e.target.value)}
                          placeholder="e.g., 2M sq ft"
                          required
                          disabled={isSubmitting}
                        />

                      </div>
                      <div className="add_field titleField">
                        <h2>Office *</h2>
                        <input
                          type="text"
                          value={formData.stats.office}
                          onChange={(e) => handleStatsChange('office', e.target.value)}
                          placeholder="e.g., 1M sq ft"
                          required
                          disabled={isSubmitting}
                        />
                        {errors['stats.office'] && (
                          <p className="error-text">{errors['stats.office']}</p>
                        )}
                      </div>
                      <div className="add_field titleField">
                        <h2>Office Description *</h2>
                        <input
                          type="text"
                          value={formData.stats.office_description}
                          onChange={(e) => handleStatsChange('office_description', e.target.value)}
                          placeholder=""
                          required
                          disabled={isSubmitting}
                        />

                      </div>
                    </div>

                    {/* About Section */}
                    <div className="section-group">
                      <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#ccc' }}>About Section *</h4>
                      {formData.about.map((item, index) => (
                        <div key={index} className="add_field textareaField" style={{ position: 'relative', marginBottom: '15px' }}>
                          {/* <h2>About Paragraph {index + 1}</h2> */}
                          <textarea
                            value={item}
                            onChange={(e) => handleAboutChange(index, e.target.value)}
                            rows="4"
                            placeholder="Enter about content"
                            disabled={isSubmitting}
                          />
                          {/* {formData.about.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeAboutField(index)}
                              style={{
                                marginTop: '5px',
                                padding: '5px 15px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px'
                              }}
                              disabled={isSubmitting}
                            >
                              Remove
                            </button>
                          )} */}
                        </div>
                      ))}
                      {/* <button
                        type="button"
                        onClick={addAboutField}
                        style={{
                          marginTop: '10px',
                          padding: '8px 16px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '4px'
                        }}
                        disabled={isSubmitting}
                      >
                        + Add About Paragraph
                      </button> */}
                      {errors.about && (
                        <p className="error-text">{errors.about}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Spanish Content */}
                {activeLanguage === 'es' && (
                  <div className="language-content">
                    <h3 style={{ color: '#fff', marginBottom: '15px' }}>Spanish Content</h3>

                    {/* Banner Section */}
                    <div className="section-group">
                      <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#ccc' }}>Banner (Español)</h4>
                      <div className="add_field titleField">
                        <h2>Etiqueta</h2>
                        <input
                          type="text"
                          value={spanishData.banner.tag}
                          onChange={(e) => handleSpanishBannerChange('tag', e.target.value)}
                          placeholder="Ingrese la etiqueta del banner"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Título</h2>
                        <input
                          type="text"
                          value={spanishData.banner.headline}
                          onChange={(e) => handleSpanishBannerChange('headline', e.target.value)}
                          placeholder="Ingrese el título del banner"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    {/* Stats Section */}
                    <div className="section-group">
                      <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#ccc' }}>Estadísticas</h4>
                      <div className="add_field titleField">
                        <h2>Millas</h2>
                        <input
                          type="text"
                          value={spanishData.stats.miles}
                          onChange={(e) => handleSpanishStatsChange('miles', e.target.value)}
                          placeholder="ej., 12,000"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Millas Description</h2>
                        <input
                          type="text"
                          value={spanishData.stats.miles_description}
                          onChange={(e) => handleSpanishStatsChange('miles_description', e.target.value)}
                          placeholder=""
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Habitaciones de Hotel</h2>
                        <input
                          type="text"
                          value={spanishData.stats.hotelRooms}
                          onChange={(e) => handleSpanishStatsChange('hotelRooms', e.target.value)}
                          placeholder="ej., 500"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Habitaciones de Hotel Description</h2>
                        <input
                          type="text"
                          value={spanishData.stats.hotelRooms_description}
                          onChange={(e) => handleSpanishStatsChange('hotelRooms_description', e.target.value)}
                          placeholder="ej., 500"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Comercio</h2>
                        <input
                          type="text"
                          value={spanishData.stats.retail}
                          onChange={(e) => handleSpanishStatsChange('retail', e.target.value)}
                          placeholder="ej., 2M pies cuadrados"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Comercio Description</h2>
                        <input
                          type="text"
                          value={spanishData.stats.retail_description}
                          onChange={(e) => handleSpanishStatsChange('retail_description', e.target.value)}
                          placeholder=""
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Oficina</h2>
                        <input
                          type="text"
                          value={spanishData.stats.office}
                          onChange={(e) => handleSpanishStatsChange('office', e.target.value)}
                          placeholder="ej., 1M pies cuadrados"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="add_field titleField">
                        <h2>Oficina Description</h2>
                        <input
                          type="text"
                          value={spanishData.stats.office_description}
                          onChange={(e) => handleSpanishStatsChange('office_description', e.target.value)}
                          placeholder=""
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    {/* About Section */}
                    <div className="section-group">
                      <h4 style={{ marginTop: '20px', marginBottom: '10px', color: '#ccc' }}>Secciones Acerca De</h4>
                      {spanishData.about.map((item, index) => (
                        <div key={index} className="add_field textareaField" style={{ position: 'relative', marginBottom: '15px' }}>
                          {/* <h2>Párrafo {index + 1}</h2> */}
                          <textarea
                            value={item}
                            onChange={(e) => handleSpanishAboutChange(index, e.target.value)}
                            rows="4"
                            placeholder="Ingrese el contenido"
                            disabled={isSubmitting}
                          />
                          {/* {spanishData.about.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeSpanishAboutField(index)}
                              style={{
                                marginTop: '5px',
                                padding: '5px 15px',
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px'
                              }}
                              disabled={isSubmitting}
                            >
                              Eliminar
                            </button>
                          )} */}
                        </div>
                      ))}
                      {/* <button
                        type="button"
                        onClick={addSpanishAboutField}
                        style={{
                          marginTop: '10px',
                          padding: '8px 16px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '4px'
                        }}
                        disabled={isSubmitting}
                      >
                        + Agregar Párrafo
                      </button> */}
                    </div>
                  </div>
                )}
              </div>

              <div className="add_project">
                <button type="submit" disabled={isSubmitting}>
                  <Save className="w-5 h-5" />
                  {isSubmitting ? 'Saving...' : isNewContent ? 'Create Home Banner' : 'Update Home Banner'}
                </button>
              </div>
            </form>

            {errors.submit && (
              <div style={{
                padding: '12px',
                marginTop: '20px',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '4px',
                color: '#721c24'
              }}>
                {errors.submit}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeBannerData;